/**
 * パスキーの管理ページ
 *
 * 登録したパスキーで、次回から Face ID / Touch ID 等だけでサインインできる。
 * パスキーは RP ID（private-media-lounge.com）に紐付くため、そのドメイン以外では登録・利用できない。
 */

import type { PasskeyListItem } from '@supabase/supabase-js'
import { Trash2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { PasskeyIcon } from '@/components/shared/auth-icons'
import { Header } from '@/components/shared/header'
import { Button } from '@/components/ui/button'
import { useGoBack } from '@/hooks/useGoBack'
import { confirmDialog } from '@/lib/confirm-dialog'
import { parseUtcDate } from '@/lib/date-utils'
import { registerPasskeyOnThisDevice } from '@/lib/passkey'
import { isPasskeyAvailableHere, PASSKEY_RP_ID, supabase } from '@/lib/supabase'
import { useAuthPreferenceStore } from '@/store/useAuthPreferenceStore'

const formatDate = (iso: string): string => parseUtcDate(iso).toLocaleString('ja-JP')

const PasskeyRow = ({
  passkey,
  onDelete,
}: {
  passkey: PasskeyListItem
  onDelete: (passkey: PasskeyListItem) => void
}) => (
  <div className="flex items-center gap-3 rounded-lg border bg-card p-4">
    <PasskeyIcon className="size-8 shrink-0 text-muted-foreground" />
    <div className="min-w-0 flex-1">
      <p className="truncate font-medium">{passkey.friendly_name || 'パスキー'}</p>
      <p className="text-xs text-muted-foreground">登録: {formatDate(passkey.created_at)}</p>
      {passkey.last_used_at && (
        <p className="text-xs text-muted-foreground">最終利用: {formatDate(passkey.last_used_at)}</p>
      )}
    </div>
    <Button variant="ghost" size="icon" aria-label="削除" onClick={() => onDelete(passkey)}>
      <Trash2 size={18} />
    </Button>
  </div>
)

export const SettingsPasskeysPage = () => {
  const goBack = useGoBack()
  const [passkeys, setPasskeys] = useState<PasskeyListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [registering, setRegistering] = useState(false)
  const canUsePasskey = isPasskeyAvailableHere()

  const fetchPasskeys = useCallback(async () => {
    const { data, error } = await supabase.auth.passkey.list()
    setLoading(false)
    if (error) {
      console.error('[Passkeys] Failed to list passkeys:', error)
      toast.error('パスキーの一覧を取得できませんでした')
      return
    }
    setPasskeys(data)
  }, [])

  useEffect(() => {
    fetchPasskeys()
  }, [fetchPasskeys])

  const handleRegister = useCallback(async () => {
    setRegistering(true)
    const result = await registerPasskeyOnThisDevice()
    setRegistering(false)
    if (result === 'cancelled') return
    if (result === 'failed') {
      toast.error('パスキーを登録できませんでした')
      return
    }
    toast.success(result === 'registered' ? 'パスキーを登録しました' : 'この端末のパスキーは登録済みです')
    await fetchPasskeys()
  }, [fetchPasskeys])

  const handleDelete = useCallback(async (passkey: PasskeyListItem) => {
    const confirmed = await confirmDialog({
      title: 'パスキーを削除しますか？',
      description: `「${passkey.friendly_name || 'パスキー'}」ではサインインできなくなります。端末やパスワードマネージャーに残ったパスキーは、そちらでも削除してください。`,
      destructive: true,
    })
    if (!confirmed) return

    const { error } = await supabase.auth.passkey.delete({ passkeyId: passkey.id })
    if (error) {
      console.error('[Passkeys] Failed to delete passkey:', error)
      toast.error('パスキーを削除できませんでした')
      return
    }
    setPasskeys((prev) => {
      const remaining = prev.filter((p) => p.id !== passkey.id)
      // 全部消したら、この端末ではパスキーを主にしない
      if (remaining.length === 0) useAuthPreferenceStore.getState().setLastSignInMethod('google')
      return remaining
    })
  }, [])

  return (
    <>
      <Header title="パスキー" onClose={goBack} />
      <div className="mx-auto max-w-2xl space-y-4 p-4">
        <p className="text-sm text-muted-foreground">
          パスキーを登録すると、次回から Face ID・Touch ID・PIN などでサインインできます。
        </p>

        {canUsePasskey ? (
          <Button className="w-full" onClick={handleRegister} disabled={registering}>
            <PasskeyIcon className="size-[18px]" />
            この端末でパスキーを登録
          </Button>
        ) : (
          <p className="rounded-lg border bg-muted p-3 text-sm text-muted-foreground">
            パスキーの登録は https://{PASSKEY_RP_ID} から開いたときだけ利用できます。
          </p>
        )}

        <div className="space-y-2">
          {!loading && passkeys.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">登録済みのパスキーはありません</p>
          )}
          {passkeys.map((passkey) => (
            <PasskeyRow key={passkey.id} passkey={passkey} onDelete={handleDelete} />
          ))}
        </div>
      </div>
    </>
  )
}
